import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser(data);
      toast.success("Account created");
      navigate("/dashboard");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F4F1EE] px-6 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="font-display text-xl text-[#1A362D]" data-testid="brand-link">← Trackr</Link>
        <h1 className="font-display text-4xl mt-8">Create your Trackr</h1>
        <p className="text-[#5C6B64] mt-2">Start tracking in less than a minute.</p>

        <Button
          onClick={handleGoogle}
          variant="outline"
          className="w-full mt-8 h-12 rounded-full border-[#1A362D]/20 bg-white hover:bg-[#F4F1EE]/50"
          data-testid="google-signup-btn"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.83z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
          </svg>
          Continue with Google
        </Button>

        <div className="flex items-center gap-4 my-6">
          <div className="flex-1 h-px bg-[#E5E1DB]" />
          <span className="text-xs tracking-[0.2em] uppercase text-[#5C6B64]">or email</span>
          <div className="flex-1 h-px bg-[#E5E1DB]" />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required
              className="h-12 mt-1.5 bg-white" data-testid="register-name-input" />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="h-12 mt-1.5 bg-white" data-testid="register-email-input" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
              className="h-12 mt-1.5 bg-white" data-testid="register-password-input" />
            <p className="text-xs text-[#5C6B64] mt-1">At least 6 characters.</p>
          </div>
          {error && <p className="text-sm text-[#C65D47]" data-testid="register-error">{error}</p>}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-full bg-[#1A362D] hover:bg-[#2C4A40] text-white text-base"
            data-testid="register-submit-btn"
          >
            {loading ? "Creating…" : "Create account"}
          </Button>
        </form>

        <p className="text-sm text-[#5C6B64] mt-6 text-center">
          Already have one?{" "}
          <Link to="/login" className="underline underline-offset-4 text-[#1A362D]" data-testid="login-link">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
